// src/components/Admin/Campaigns/ai/campaignAssist.ts
//
// The grounded-chat directive protocol for the Campaign Builder Guide.
//
// The chat endpoint has no tool/function calling, so anything structured has to
// come back inside the reply text. The Guide is asked to append a single fenced
// ```json block in a known shape; we strip it out of what the admin reads and
// turn it into Apply buttons.
//
// Nothing is ever written to a campaign automatically. The model is drafting and
// can get things wrong; the admin confirms. Two safety rails enforce that here:
//   - Only whitelisted fields (campaign vs. step) survive coercion.
//   - A step edit must name the CURRENTLY SELECTED step (`step:<id>`), so the
//     model cannot reach across the tree and rewrite a step the admin is not
//     looking at.

import {
  Campaign,
  CampaignStep,
  STEP_KINDS,
  STEP_TRACKS,
  buildStepTree,
  statusLabel,
} from '../../../../support/campaigns';
import { StepStub } from './campaignFlows';

// ---------------------------------------------------------------------------
// Directive shape
// ---------------------------------------------------------------------------

export interface AssistDirective {
  /** Proposed edits to the campaign (`target: 'campaign'`) or a step (`target: 'step:<id>'`). */
  edits?: Array<{
    target: 'campaign' | string;
    field: string;
    value: string | number | boolean;
  }>;
  /** Proposed brand-new steps to add. */
  newSteps?: StepStub[];
}

// ---------------------------------------------------------------------------
// Whitelists
// ---------------------------------------------------------------------------

export const ALLOWED_CAMPAIGN_FIELDS: string[] = [
  'name',
  'description',
  'estimated_duration_minutes',
  'status',
];

export const ALLOWED_STEP_FIELDS: string[] = [
  'name',
  'description',
  'track',
  'step_kind',
  'is_required',
  'estimated_duration_minutes',
  'launch_url',
  'provider',
];

const ALLOWED_STEP_KINDS = new Set<string>(STEP_KINDS.map((k) => k.value));
const ALLOWED_STEP_TRACKS = new Set<string>(STEP_TRACKS.map((t) => t.value));

const FENCE = /```json\s*([\s\S]*?)```/gi;

const STYLE_RULES =
  'Keep replies SHORT and conversational: two to five short sentences, or a tight bulleted list, ' +
  'the way a person answers in a live chat, never a long essay. Use light markdown only where it ' +
  'genuinely helps. Never invent customer facts, product details, or metrics that are not in the ' +
  'context above — if something is unknown, say so. Only go longer if the user explicitly asks.';

// ---------------------------------------------------------------------------
// Coercion helpers
// ---------------------------------------------------------------------------

const cleanStr = (value: unknown, maxLen: number): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, maxLen);
};

/** Validate/clamp a proposed value against the field it targets. */
function coerceFieldValue(
  field: string,
  value: unknown
): string | number | boolean | undefined {
  switch (field) {
    case 'is_required':
      return typeof value === 'boolean' ? value : undefined;
    case 'estimated_duration_minutes': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return Math.min(Math.round(n), 100000);
    }
    case 'status': {
      const v = cleanStr(value, 40);
      // Only the campaign lifecycle statuses are proposable.
      return v && ['draft', 'published', 'archived'].includes(v) ? v : undefined;
    }
    case 'track': {
      const v = cleanStr(value, 40);
      return v && ALLOWED_STEP_TRACKS.has(v) ? v : undefined;
    }
    case 'step_kind': {
      const v = cleanStr(value, 40);
      return v && ALLOWED_STEP_KINDS.has(v) ? v : undefined;
    }
    case 'description':
      return cleanStr(value, 2000);
    default:
      return cleanStr(value, 500);
  }
}

function coerceEdits(
  value: unknown,
  allowed: { campaignFields: string[]; stepFields: string[]; selectedStepId: number | null }
): AssistDirective['edits'] {
  if (!Array.isArray(value)) return undefined;

  const campaignFields = new Set(allowed.campaignFields || []);
  const stepFields = new Set(allowed.stepFields || []);
  const out: NonNullable<AssistDirective['edits']> = [];

  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const rawTarget = (entry as { target?: unknown }).target;
    const rawField = (entry as { field?: unknown }).field;
    if (typeof rawTarget !== 'string' || typeof rawField !== 'string') continue;

    const target = rawTarget.trim();
    const field = rawField.trim();

    let allowedFields: Set<string>;
    if (target === 'campaign') {
      allowedFields = campaignFields;
    } else if (target.startsWith('step:')) {
      // A step edit must name the currently selected step, or it is dropped.
      const id = Number(target.slice('step:'.length));
      if (
        allowed.selectedStepId == null ||
        !Number.isFinite(id) ||
        id !== allowed.selectedStepId
      ) {
        continue;
      }
      allowedFields = stepFields;
    } else {
      continue; // unknown target format
    }

    if (!allowedFields.has(field)) continue;

    const coerced = coerceFieldValue(field, (entry as { value?: unknown }).value);
    if (coerced === undefined) continue;

    out.push({ target, field, value: coerced });
  }

  return out.length > 0 ? out : undefined;
}

function coerceNewSteps(value: unknown): StepStub[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: StepStub[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as Record<string, unknown>;
    const name = cleanStr(r.name, 200);
    if (!name) continue;

    const stub: StepStub = { name };
    const description = cleanStr(r.description, 2000);
    if (description) stub.description = description;

    const track = cleanStr(r.track, 40);
    if (track && ALLOWED_STEP_TRACKS.has(track)) stub.track = track;

    const kind = cleanStr(r.step_kind, 40);
    if (kind && ALLOWED_STEP_KINDS.has(kind)) stub.step_kind = kind;

    if (typeof r.is_required === 'boolean') stub.is_required = r.is_required;

    out.push(stub);
    if (out.length >= 20) break;
  }
  return out.length > 0 ? out : undefined;
}

// ---------------------------------------------------------------------------
// parseReply
// ---------------------------------------------------------------------------

/**
 * Pull the directive out of a Guide reply and return the visible text with any
 * fenced ```json block removed. Malformed JSON is not an error: the block is
 * left in place and the reply renders as ordinary text.
 */
export function parseReply(
  raw: string,
  allowed: {
    campaignFields: string[];
    stepFields: string[];
    selectedStepId: number | null;
  }
): { text: string; directive: AssistDirective | null } {
  if (typeof raw !== 'string') return { text: '', directive: null };

  let directive: AssistDirective | null = null;
  let text = raw;

  const matches = [...raw.matchAll(FENCE)];
  for (const match of matches) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(match[1]);
    } catch {
      continue; // leave malformed block visible
    }
    if (!parsed || typeof parsed !== 'object') continue;

    const edits = coerceEdits((parsed as { edits?: unknown }).edits, allowed);
    const newSteps = coerceNewSteps((parsed as { newSteps?: unknown }).newSteps);
    if (!edits && !newSteps) continue;

    directive = {
      ...(edits ? { edits } : {}),
      ...(newSteps ? { newSteps } : {}),
    };
    text = text.replace(match[0], '').trim();
  }

  return { text, directive };
}

// ---------------------------------------------------------------------------
// buildGrounding
// ---------------------------------------------------------------------------

/**
 * The context preamble the Guide is grounded on. Sent once per selection change
 * (the backend holds conversation state per session_id), it describes the
 * campaign, its step tree, the selected step and which fields may be proposed,
 * and the style rules.
 */
export function buildGrounding(input: {
  campaign: Campaign;
  steps: CampaignStep[];
  selectedStep: CampaignStep | null;
}): string {
  const { campaign, steps, selectedStep } = input;

  const treeLines: string[] = [];
  const walk = (nodes: ReturnType<typeof buildStepTree>, depth: number) => {
    for (const node of nodes) {
      const indent = '  '.repeat(depth);
      const marker = selectedStep && node.id === selectedStep.id ? ' <-- selected' : '';
      treeLines.push(
        `${indent}- step:${node.id} "${node.name}" ` +
          `[track=${node.track}, kind=${node.step_kind}` +
          `${node.is_required ? ', required' : ''}]${marker}`
      );
      if (node.children && node.children.length) walk(node.children, depth + 1);
    }
  };
  walk(buildStepTree(steps || []), 0);

  const selectedBlock = selectedStep
    ? [
        `Selected step: step:${selectedStep.id} "${selectedStep.name}".`,
        `You may propose edits to these step fields: ${ALLOWED_STEP_FIELDS.join(', ')}.`,
        `Target a step edit as "step:${selectedStep.id}". Do not edit any other step.`,
      ].join('\n')
    : 'No step is selected. You may propose campaign-level edits and brand-new steps, but not edits to a specific step.';

  return [
    'You are the Campaign Builder Guide inside an internal admin tool. You are helping an admin ' +
      'design a reusable enablement/delivery campaign. You are talking to the admin, not an end user.',
    `Campaign: "${campaign.name}" (status: ${statusLabel(campaign.status)}).`,
    campaign.description ? `Campaign description: ${campaign.description}` : '',
    'Step tree (indented by parent, ordered as shown):',
    treeLines.length ? treeLines.join('\n') : '(no steps yet)',
    selectedBlock,
    `You may propose campaign-level edits to these fields: ${ALLOWED_CAMPAIGN_FIELDS.join(', ')}.`,
    'When you want the admin to change something, append a SINGLE fenced ```json block at the end ' +
      'of your reply in exactly this shape (omit keys you are not proposing):',
    '```json\n' +
      JSON.stringify(
        {
          edits: [
            { target: 'campaign', field: 'description', value: 'proposed value' },
            { target: `step:${selectedStep?.id ?? '<id>'}`, field: 'name', value: 'proposed name' },
          ],
          newSteps: [
            { name: 'New step', description: '...', track: 'enablement', step_kind: 'content' },
          ],
        },
        null,
        2
      ) +
      '\n```',
    'Never apply changes yourself; the admin reviews every proposal. Only propose fields listed above.',
    STYLE_RULES,
  ]
    .filter(Boolean)
    .join('\n\n');
}
